import { useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Loader2, MapPin, Package, Plus, Truck, X } from 'lucide-react';
import { toast } from 'sonner';
import useGeocoding from '@/hooks/useGeocoding';

const PROVINCES = [
  'AB','BC','MB','NB','NL','NS','NT','NU','ON','PE','QC','SK','YT',
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID',
  'IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO',
  'MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA',
  'RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];
const EQUIPMENT = ['Cargo Van','Straight Truck','Truck','Flatbed','Reefer','Tanker'];

function getVehicleType(weight) {
  if (weight <= 5000) return 'Cargo Van';
  if (weight <= 15000) return 'Straight Truck';
  if (weight <= 30000) return 'Truck';
  if (weight <= 44000) return 'Flatbed';
  return 'Truck';
}

const PACKAGE_TYPES = ['box', 'pallet', 'skid', 'crate', 'drum', 'other'];
const emptyPackage = { package_type: 'box', quantity: 1, weight_lbs: '', length_in: '', width_in: '', height_in: '', description: '' };

function AddressColumn({ title, icon: Icon, data, onChange, geo }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-brand-accent" />
        <span className="text-sm font-semibold text-foreground">{title}</span>
      </div>

      <div>
        <Label className="text-xs text-muted-foreground">Location Name</Label>
        <Input value={data.name} onChange={e => onChange({ ...data, name: e.target.value })}
          placeholder="e.g. Main Warehouse" className="bg-muted border-input text-foreground mt-1 h-8 text-xs" />
      </div>

      <div className="relative">
        <Label className="text-xs text-muted-foreground">Address Search</Label>
        <Input
          value={data.search}
          onChange={e => { onChange({ ...data, search: e.target.value }); geo.geocode(e.target.value); }}
          placeholder="Start typing address..."
          className="bg-muted border-input text-foreground mt-1 h-8 text-xs"
        />
        {geo.suggestions.length > 0 && (
          <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-muted border border-input rounded-lg shadow-xl max-h-64 overflow-auto">
            {geo.suggestions.map((s, i) => (
              <button key={i} className="w-full text-left px-3 py-2 text-xs text-muted-foreground hover:bg-muted truncate"
                onClick={() => {
                  const parsed = geo.parseSuggestion(s);
                  onChange({
                    ...data,
                    search: parsed.fullAddress,
                    street: parsed.street, city: parsed.city,
                    province: parsed.province, postal: parsed.postal,
                    lat: parsed.lat, lng: parsed.lng,
                  });
                  geo.clearSuggestions();
                }}>
                {s.display_name}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <div className="flex-1">
          <Label className="text-xs text-muted-foreground">Street</Label>
          <Input value={data.street} onChange={e => onChange({ ...data, street: e.target.value })}
            className="bg-muted border-input text-foreground mt-1 h-8 text-xs" />
        </div>
        <div className="w-24">
          <Label className="text-xs text-muted-foreground">Unit/Suite #</Label>
          <Input value={data.unit} onChange={e => onChange({ ...data, unit: e.target.value })}
            placeholder="e.g. 200" className="bg-muted border-input text-foreground mt-1 h-8 text-xs" />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label className="text-xs text-muted-foreground">City</Label>
          <Input value={data.city} onChange={e => onChange({ ...data, city: e.target.value })}
            className="bg-muted border-input text-foreground mt-1 h-8 text-xs" />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Province</Label>
          <select value={data.province} onChange={e => onChange({ ...data, province: e.target.value })}
            className="w-full mt-1 bg-muted border border-input text-foreground text-xs rounded-md px-2 py-1.5 h-8">
            <option value="">--</option>
            {PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Postal</Label>
          <Input value={data.postal} onChange={e => onChange({ ...data, postal: e.target.value })}
            className="bg-muted border-input text-foreground mt-1 h-8 text-xs" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs text-muted-foreground">Contact Name</Label>
          <Input value={data.contactName} onChange={e => onChange({ ...data, contactName: e.target.value })}
            className="bg-muted border-input text-foreground mt-1 h-8 text-xs" />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Contact Phone</Label>
          <Input value={data.contactPhone} onChange={e => onChange({ ...data, contactPhone: e.target.value })}
            className="bg-muted border-input text-foreground mt-1 h-8 text-xs" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs text-muted-foreground">Date</Label>
          <Input type="date" value={data.date} onChange={e => onChange({ ...data, date: e.target.value })}
            className="bg-muted border-input text-foreground mt-1 h-8 text-xs" />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Hours</Label>
          <Input value={data.hours} onChange={e => onChange({ ...data, hours: e.target.value })}
            placeholder="e.g. 8AM-5PM" className="bg-muted border-input text-foreground mt-1 h-8 text-xs" />
        </div>
      </div>

      {data.lat && data.lng && (
        <div className="text-[10px] text-emerald-400 flex items-center gap-1">
          <MapPin className="w-3 h-3" /> Coordinates: {data.lat.toFixed(4)}, {data.lng.toFixed(4)}
        </div>
      )}
    </div>
  );
}

const emptyAddr = { name: '', search: '', street: '', unit: '', city: '', province: '', postal: '', lat: null, lng: null, contactName: '', contactPhone: '', date: '', hours: '' };

export default function CreateLoadDialog({ open, onOpenChange, onCreated }) {
  const { token } = useAuth();
  const [pickup, setPickup] = useState({ ...emptyAddr });
  const [delivery, setDelivery] = useState({ ...emptyAddr });
  const [cargo, setCargo] = useState({ description: '', weight: '', boxCount: '', palletCount: '', deliveryType: 'regular', equipmentType: '', notes: '', rate: '', distance: '', billTo: '', specialInstructions: '' });
  const [packages, setPackages] = useState([{ ...emptyPackage }]);
  const [saving, setSaving] = useState(false);
  const pickupGeo = useGeocoding(token);
  const deliveryGeo = useGeocoding(token);

  const handleWeightChange = (val) => {
    const w = parseFloat(val) || 0;
    setCargo(prev => ({ ...prev, weight: val, equipmentType: w > 0 ? getVehicleType(w) : prev.equipmentType }));
  };

  const updatePackage = (index, field, value) => {
    setPackages(prev => prev.map((p, i) => i === index ? { ...p, [field]: value } : p));
  };
  const addPackage = () => setPackages(prev => [...prev, { ...emptyPackage }]);
  const removePackage = (index) => setPackages(prev => prev.filter((_, i) => i !== index));

  const handleSubmit = useCallback(async () => {
    if (!pickup.lat || !delivery.lat || !cargo.description || !cargo.weight) {
      toast.error('Fill in addresses and cargo details');
      return;
    }
    setSaving(true);
    try {
            const fullPickupAddr = [pickup.street, pickup.city, pickup.province, pickup.postal].filter(Boolean).join(', ');
      const fullDeliveryAddr = [delivery.street, delivery.city, delivery.province, delivery.postal].filter(Boolean).join(', ');
      const res = await api.post('/loads', {
        pickup_name: pickup.name, pickup_address: fullPickupAddr || pickup.search,
        pickup_lat: pickup.lat, pickup_lng: pickup.lng,
        pickup_street: pickup.street, pickup_unit: pickup.unit || undefined,
        pickup_city: pickup.city,
        pickup_province: pickup.province, pickup_postal: pickup.postal,
        shipper_contact_name: pickup.contactName, shipper_contact_phone: pickup.contactPhone,
        pickup_date: pickup.date || undefined, pickup_hours: pickup.hours || undefined,
        delivery_name: delivery.name, delivery_address: fullDeliveryAddr || delivery.search,
        delivery_lat: delivery.lat, delivery_lng: delivery.lng,
        delivery_street: delivery.street, delivery_unit: delivery.unit || undefined,
        delivery_city: delivery.city,
        delivery_province: delivery.province, delivery_postal: delivery.postal,
        receiver_contact_name: delivery.contactName, receiver_contact_phone: delivery.contactPhone,
        delivery_date: delivery.date || undefined, delivery_hours: delivery.hours || undefined,
        cargo_description: cargo.description,
        weight_lbs: parseFloat(cargo.weight) || 0,
        rate: parseFloat(cargo.rate) || 0,
        distance_miles: parseFloat(cargo.distance) || 0,
        delivery_type: cargo.deliveryType,
        equipment_type_required: cargo.equipmentType || undefined,
        box_count: parseInt(cargo.boxCount) || undefined,
        pallet_count: parseInt(cargo.palletCount) || undefined,
        notes: cargo.notes || undefined,
        special_instructions: cargo.specialInstructions || undefined,
        bill_to: cargo.billTo || undefined,
        packages: packages.filter(p => p.weight_lbs || p.description).map(p => ({
          ...p,
          quantity: parseInt(p.quantity) || 1,
          weight_lbs: parseFloat(p.weight_lbs) || 0,
          length_in: parseFloat(p.length_in) || 0,
          width_in: parseFloat(p.width_in) || 0,
          height_in: parseFloat(p.height_in) || 0,
        })),
      });
      // Auto-post to posted status
      await api.put(`/loads/${res.data.load_id}/status`, { status: 'posted' });
      // Auto-generate charges from pricing rules
      await api.post(`/loads/${res.data.load_id}/charges/auto-generate`).catch(() => {});
      toast.success('Load created successfully');
      setPickup({ ...emptyAddr });
      setDelivery({ ...emptyAddr });
      setCargo({ description: '', weight: '', boxCount: '', palletCount: '', deliveryType: 'regular', equipmentType: '', notes: '', rate: '', distance: '', billTo: '', specialInstructions: '' });
      setPackages([{ ...emptyPackage }]);
      onOpenChange(false);
      if (onCreated) onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create load');
    } finally {
      setSaving(false);
    }
  }, [token, pickup, delivery, cargo, packages, onOpenChange, onCreated]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-border text-foreground max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-brand-accent" /> Create New Load
          </DialogTitle>
        </DialogHeader>

        {/* Two-column: Ship From / Ship To */}
        <div className="grid grid-cols-2 gap-6 mt-4">
          <div className="bg-muted/30 border border-input/50 rounded-xl p-4">
            <AddressColumn title="Ship From" icon={MapPin} data={pickup} onChange={setPickup} geo={pickupGeo} />
          </div>
          <div className="bg-muted/30 border border-input/50 rounded-xl p-4">
            <AddressColumn title="Ship To" icon={MapPin} data={delivery} onChange={setDelivery} geo={deliveryGeo} />
          </div>
        </div>

        {/* Cargo section */}
        <div className="bg-muted/30 border border-input/50 rounded-xl p-4 mt-4 space-y-3">
          <div className="flex items-center gap-2 mb-2">
            <Truck className="w-4 h-4 text-brand-accent" />
            <span className="text-sm font-semibold text-foreground">Cargo & Pricing</span>
          </div>

          <div>
            <Label className="text-xs text-muted-foreground">Cargo Description</Label>
            <textarea value={cargo.description} onChange={e => setCargo({ ...cargo, description: e.target.value })}
              className="w-full mt-1 bg-muted border border-input text-foreground text-xs rounded-md px-3 py-2 h-16 resize-none"
              placeholder="What's being shipped?" />
          </div>

          <div className="grid grid-cols-5 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Weight (lbs)</Label>
              <Input type="number" value={cargo.weight} onChange={e => handleWeightChange(e.target.value)}
                className="bg-muted border-input text-foreground mt-1 h-8 text-xs" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Boxes</Label>
              <Input type="number" value={cargo.boxCount} onChange={e => setCargo({ ...cargo, boxCount: e.target.value })}
                className="bg-muted border-input text-foreground mt-1 h-8 text-xs" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Pallets</Label>
              <Input type="number" value={cargo.palletCount} onChange={e => setCargo({ ...cargo, palletCount: e.target.value })}
                className="bg-muted border-input text-foreground mt-1 h-8 text-xs" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Delivery Type</Label>
              <select value={cargo.deliveryType} onChange={e => setCargo({ ...cargo, deliveryType: e.target.value })}
                className="w-full mt-1 bg-muted border border-input text-foreground text-xs rounded-md px-2 py-1.5 h-8">
                <option value="regular">Regular</option>
                <option value="rush">Rush</option>
                <option value="direct">Direct</option>
              </select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Equipment</Label>
              <select value={cargo.equipmentType} onChange={e => setCargo({ ...cargo, equipmentType: e.target.value })}
                className="w-full mt-1 bg-muted border border-input text-foreground text-xs rounded-md px-2 py-1.5 h-8">
                <option value="">Auto</option>
                {EQUIPMENT.map(e => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
          </div>

          {/* Packages section */}
          <div className="space-y-2 pt-2 border-t border-input/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-brand-accent" />
                <span className="text-xs font-semibold text-foreground">Packages</span>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={addPackage}
                className="h-7 text-xs px-2 gap-1 border-input text-muted-foreground hover:text-foreground">
                <Plus className="w-3 h-3" /> Add Package
              </Button>
            </div>
            {packages.map((pkg, idx) => (
              <div key={idx} className="flex items-end gap-2 bg-muted/50 rounded-lg p-2">
                <div className="w-24">
                  <Label className="text-[10px] text-muted-foreground">Type</Label>
                  <select value={pkg.package_type} onChange={e => updatePackage(idx, 'package_type', e.target.value)}
                    className="w-full mt-0.5 bg-muted border border-input text-foreground text-xs rounded-md px-1.5 py-1 h-7">
                    {PACKAGE_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                  </select>
                </div>
                <div className="w-14">
                  <Label className="text-[10px] text-muted-foreground">Qty</Label>
                  <Input type="number" min="1" value={pkg.quantity} onChange={e => updatePackage(idx, 'quantity', e.target.value)}
                    className="bg-muted border-input text-foreground mt-0.5 h-7 text-xs px-1.5" />
                </div>
                <div className="w-20">
                  <Label className="text-[10px] text-muted-foreground">Weight (lbs)</Label>
                  <Input type="number" value={pkg.weight_lbs} onChange={e => updatePackage(idx, 'weight_lbs', e.target.value)}
                    className="bg-muted border-input text-foreground mt-0.5 h-7 text-xs px-1.5" />
                </div>
                <div className="flex items-end gap-1">
                  <div className="w-14">
                    <Label className="text-[10px] text-muted-foreground">L (in)</Label>
                    <Input type="number" value={pkg.length_in} onChange={e => updatePackage(idx, 'length_in', e.target.value)}
                      className="bg-muted border-input text-foreground mt-0.5 h-7 text-xs px-1.5" />
                  </div>
                  <span className="text-xs text-muted-foreground pb-1.5">&times;</span>
                  <div className="w-14">
                    <Label className="text-[10px] text-muted-foreground">W (in)</Label>
                    <Input type="number" value={pkg.width_in} onChange={e => updatePackage(idx, 'width_in', e.target.value)}
                      className="bg-muted border-input text-foreground mt-0.5 h-7 text-xs px-1.5" />
                  </div>
                  <span className="text-xs text-muted-foreground pb-1.5">&times;</span>
                  <div className="w-14">
                    <Label className="text-[10px] text-muted-foreground">H (in)</Label>
                    <Input type="number" value={pkg.height_in} onChange={e => updatePackage(idx, 'height_in', e.target.value)}
                      className="bg-muted border-input text-foreground mt-0.5 h-7 text-xs px-1.5" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <Label className="text-[10px] text-muted-foreground">Description</Label>
                  <Input value={pkg.description} onChange={e => updatePackage(idx, 'description', e.target.value)}
                    placeholder="Optional" className="bg-muted border-input text-foreground mt-0.5 h-7 text-xs px-1.5" />
                </div>
                {packages.length > 1 && (
                  <button type="button" onClick={() => removePackage(idx)}
                    className="p-1 text-muted-foreground hover:text-red-400 transition-colors mb-0.5">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-4 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Rate ($)</Label>
              <Input type="number" step="0.01" value={cargo.rate} onChange={e => setCargo({ ...cargo, rate: e.target.value })}
                className="bg-muted border-input text-foreground mt-1 h-8 text-xs" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Distance (miles)</Label>
              <Input type="number" value={cargo.distance} onChange={e => setCargo({ ...cargo, distance: e.target.value })}
                className="bg-muted border-input text-foreground mt-1 h-8 text-xs" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Bill To</Label>
              <Input value={cargo.billTo} onChange={e => setCargo({ ...cargo, billTo: e.target.value })}
                className="bg-muted border-input text-foreground mt-1 h-8 text-xs" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Special Instructions</Label>
              <Input value={cargo.specialInstructions} onChange={e => setCargo({ ...cargo, specialInstructions: e.target.value })}
                className="bg-muted border-input text-foreground mt-1 h-8 text-xs" />
            </div>
          </div>
        </div>

        <Button onClick={handleSubmit} disabled={saving} className="w-full bg-brand-accent text-brand-accent-foreground font-bold mt-4 h-10">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Package className="w-4 h-4 mr-2" />}
          {saving ? 'Creating...' : 'Create Load'}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
